import React from 'react';
import * as Yup from 'yup';
import { Button, RadioGroup } from '@economic/taco';
import { Trans } from '@lingui/macro';

import { useCurrentUser } from '@economic/react-app';
import { Field, Form } from '../../shared/components/Form';
import { t, tBB } from '../../shared/components/l10n/l10n';
import useMutation from '../../shared/hooks/useMutation';
import { cvrLookupInternal } from '../../shared/util/utilities/cvrLookup';
import { debouncePromise } from '../../shared/util/utilities/debouncePromise';
import { getCityNameFromPostCodeInternal } from '../../shared/util/utilities/getCityNameFromPostcode';
import { isValidCvr } from '../../shared/util/utilities/cvrUtils';
import { OnboardingStatusCompanyData } from './types';

const updateCompanyData = useMutation('onboarding/company-data', 'PUT');

const fetchCityFromZipcode = debouncePromise(getCityNameFromPostCodeInternal);

const fetchCompanyInfoByCvr = debouncePromise(cvrLookupInternal);

interface Props {
    onDone: () => void;
    setHasAccountant: (value) => void;
    companyData: OnboardingStatusCompanyData;
    hasAccountant: boolean | undefined;
    children: string | JSX.Element;
}

export default function StepBusinessInfo({ companyData, onDone, setHasAccountant, hasAccountant, children }: Props) {
    const user = useCurrentUser();

    if (!user) {
        return null;
    }

    async function saveBusinessInfo(values) {
        await updateCompanyData(values);
        onDone();
    }

    const updates = [
        {
            field: 'cvr',
            updates: async value => {
                if (user.agreement.edition === 1) {
                    if (!isValidCvr) {
                        return {};
                    }

                    try {
                        const data = await fetchCompanyInfoByCvr(value);
                        if (!!data && `${data.cvr}` === value) {
                            return {
                                name: data.name,
                                address: data.address.lines,
                                cvr: data.cvr,
                                phone: data.phone,
                                email: data.email,
                                zipCode: data.address.postcode,
                                city: data.address.city,
                            };
                        }
                    } catch (e) {
                        // do nothing, this is autocomplete
                    }
                }
                return {};
            },
        },
        {
            field: 'zipCode',
            updates: async zipCode => {
                try {
                    const city = await fetchCityFromZipcode(zipCode);
                    if (city) {
                        return { city };
                    }
                } catch (e) {
                    // do nothing, this is autocomplete
                }

                return {};
            },
        },
    ];

    const businessInfoValidationSchema = Yup.object().shape({
        contact: Yup.string().required(),
        name: Yup.string().required(),
        email: Yup.string()
            .email(t`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_EMAIL_VALIDATION_MESSAGE`)
            .required(),
        zipCode: Yup.string().required(),
        city: Yup.string().required(),
        phone: Yup.string()
            .matches(/^\+?[\d-. ()/]+$/, t`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_PHONE_VALIDATION_MESSAGE`)
            .required(),
        address: Yup.string().required(),
    });

    return (
        <div className="onboarding__container__fixed-size">
            <div className="onboarding onboarding__business-info">
                {children}

                <h2>{t`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_TITLE`}</h2>

                <p>{t`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_CONTENT`}</p>
                <Form
                    skipNotifications
                    onSubmit={saveBusinessInfo}
                    updates={updates}
                    initialData={{
                        name: companyData.name,
                        cvr: companyData.cvr,
                        address: companyData.address,
                        zipCode: companyData.zipCode,
                        city: companyData.city,
                        contact: companyData.contact,
                        email: companyData.email,
                        phone: companyData.phone,
                        website: companyData.website,
                    }}
                    validationSchema={businessInfoValidationSchema}>
                    <div className="onboarding__form-content">
                        <div className="columns">
                            <Field.Text name="cvr" label={tBB(`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_CVR`)} maxLength={8} />
                            <Field.Text
                                name="contact"
                                label={t`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_CONTACTPERSON`}
                                required
                                maxLength={50}
                            />
                        </div>
                        <div className="columns">
                            <Field.Text
                                name="name"
                                label={t`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_NAME`}
                                required
                                maxLength={100}
                            />
                            <Field.Text
                                name="email"
                                label={t`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_EMAIL`}
                                required
                                maxLength={50}
                            />
                        </div>
                        <div className="columns">
                            <Field.Text
                                name="address"
                                label={t`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_ADDRESS`}
                                required
                                maxLength={200}
                            />
                            <Field.Text
                                name="phone"
                                label={t`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_PHONE`}
                                required
                                maxLength={20}
                            />
                        </div>
                        <div className="columns">
                            <div className="columns">
                                <Field.Text
                                    name="zipCode"
                                    label={t`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_ZIPCODE`}
                                    required
                                    maxLength={10}
                                />
                                <Field.Text
                                    name="city"
                                    label={t`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_CITY`}
                                    required
                                    maxLength={50}
                                />
                            </div>
                            <Field.Text
                                name="website"
                                label={tBB(`ONBOARDING_GETTINGSTARTED_BUSINESSINFO_WEBSITE`)}
                                maxLength={80}
                            />
                        </div>
                        <div className="flex flex-col items-center">
                            <Trans>Does your company have an accountant/bookkeeper?</Trans>
                            <RadioGroup
                                name="economicGoRadio"
                                orientation="horizontal"
                                className="onboarding__radio-buttons"
                                value={hasAccountant}
                                onChange={value => {
                                    setHasAccountant(value);
                                }}>
                                <RadioGroup.Item value>{t`ECONOMIC_GO_YES`}</RadioGroup.Item>
                                <RadioGroup.Item value={false}>{t`ECONOMIC_GO_NO`}</RadioGroup.Item>
                            </RadioGroup>
                        </div>
                    </div>
                    <div className="onboarding__navigation-buttons">
                        <Button appearance="primary" type="submit">{t`ONBOARDING_GETTINGSTARTED_NEXT`}</Button>
                    </div>
                </Form>
            </div>
        </div>
    );
}
